import { productPlaceholder } from '@assets/placeholders'
import { useModalAction } from '@components/common/modal/modal.context'
import Image from '@components/ui/image'
import { useCart } from '@contexts/cart/cart.context'
import usePrice from '@framework/product/use-price'
import { Product } from '@framework/types'
import { convertProduct } from '@framework/utils/convert-product'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { FC, useContext, useState, useEffect } from 'react'

import Counter from '@components/ui/counter'
import { CommonProductTitle } from '@utils/common-product-title'
import { generateCartItem } from '@utils/generate-cart-item'
import {
  createPricePayload,
  getCurrentPrice,
  getDiscountedPrice,
  getOldPrice,
  getShipInDays,
  isSpecialOrder,
} from '@utils/helper'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { IoStarSharp } from 'react-icons/io5'
import StarRatingComponent from 'react-star-rating-component'
import { UserContext } from '@contexts/user/user.context'
import { IUser } from '@contexts/user/user.utils'
import { toast } from 'react-toastify'
import { useSession } from 'next-auth/react'

const AddToCart = dynamic(() => import('@components/product/add-to-cart'), {
  ssr: false,
})
interface ProductProps {
  product: Product
  className?: string
  page?: string
  uniqueKey?: string
  resetTrigger?: number
  allowModal?: boolean
}

const ProductCard = ({
  product,
  gallery,
  name,
  brand,
  productUrl,
  page = 'home',
  uniqueKey = '',
  resetTrigger,
}) => {
  const router = useRouter()
  const { data: session } = useSession()
  const [selectedQuantity, setSelectedQuantity] = useState<string | number>(1)
  const { closeModal } = useModalAction()
  const { t: translate } = useTranslation('common')
  const { addItemToCart, items } = useCart()
  const {
    case_quantity,
    stock_type,
    shipping_type,
    is_active,
    is_in_stock,
    flags,
    ships_in_days,
    price,
    special_price,
    special_from_date,
    special_to_date,
    mag_attributes,
    attributes,
    catalog_rule_price,
    sold_as,
  } = convertProduct(product) || {}

  const { user, isHomeBuyer, addToCartLoader, setAddToCartLoader } = useContext(
    UserContext,
  ) as IUser
  const isInStock = is_in_stock || false
  const isNewArrival = flags?.includes('new_arrivals') || false
  const isRebateEligible =
    (!isHomeBuyer && flags?.includes('rebate_eligible')) || false
  const isClearance = flags?.includes('liquidation') || false
  const isFreeShipping = shipping_type === 'Ships Free'
  const isSpecialOrderFlag = isSpecialOrder(product)

  const isCase = String(sold_as).toLowerCase().includes('ca')
  const displaySoldAsText = isCase ? translate('text-per-case') : sold_as

  const isBuyMoreSaveMore =
    mag_attributes?.find((item) => item.attribute_code === 'tier_prices')?.value
      ?.length > 0

  const shipsInDays = getShipInDays(ships_in_days, translate)

  const priceDetails = {
    special_price,
    special_from_date,
    special_to_date,
    price,
  }

  const currentPrice = getCurrentPrice(createPricePayload(product))
  const oldPrice = getOldPrice(createPricePayload(product))

  const tierPrices = product?.mag_attributes?.find(
    (attr: { attribute_code: string }) => attr.attribute_code === 'tier_prices',
  )?.value

  const finalCurrentPrice = getDiscountedPrice(
    user?.group_id,
    catalog_rule_price,
    currentPrice,
    session,
    tierPrices,
  )

  const { price: formattedCurrentPrice } = usePrice({
    amount: finalCurrentPrice,
    baseAmount: price,
    currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
  })

  const { price: formattedOldPrice } = usePrice({
    amount: oldPrice ?? 0, // If old price is null, pass 0 to the hook
    baseAmount: price,
    currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
  })

  // eslint-disable-next-line no-undef
  const inputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    // Allow empty string for typing
    if (value === '') {
      setSelectedQuantity('')
      return
    }

    // Allow only digits (no letters or symbols)
    if (/^\d+$/.test(value)) {
      const numericValue = parseInt(value, 10)

      if (numericValue < 1) {
        setSelectedQuantity('')
      } else {
        setSelectedQuantity(numericValue.toString())
      }
    }
  }

  function addToCart() {
    if (product?.min_sale_qty && selectedQuantity < product?.min_sale_qty) {
      if (!items.find((item) => item.id === product.sku)) {
        toast.error(
          `${translate('text-fewest-you-may-purchase')} ${
            product?.min_sale_qty
          }`,
          {
            className: 'toast-error',
          },
        )
        return
      }
    }
    const item = generateCartItem(product!)
    const updatedItem = {
      ...item,
    }
    addItemToCart(updatedItem, selectedQuantity)
  }

  // eslint-disable-next-line no-undef
  function navigateToProductPage(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault()
    event.stopPropagation()
    closeModal()
    if (router.pathname === '/[product_pdp]') {
      window.location.href = productUrl
    } else router.push(productUrl)
  }
  useEffect(() => {
    setSelectedQuantity(1)
  }, [resetTrigger])
  /**
   * Safely extracts and returns the product rating from attributes.
   *
   * This is the best approach because:
   * 1. It handles all edge cases (missing data, invalid JSON, wrong data types)
   * 2. It won't throw unexpected errors
   * 3. It provides consistent fallback behavior
   * 4. It logs errors for debugging while maintaining runtime safety
   *
   * @param attributes - Object containing optional product_review string
   * @returns The product rating as number (0 if unavailable/invalid)
   */
  function getProductRating(attributes: { product_review?: string }): number {
    try {
      // 1. Safely attempt to parse the review JSON only if it exists
      //    Using optional chaining (?.) prevents errors if attributes is null/undefined
      const review = attributes?.product_review
        ? JSON.parse(attributes.product_review)
        : null

      // 2. Safely extract rating_avg with optional chaining
      const rating = review?.rating_avg

      // 3. Explicit type check ensures we only return a number
      //    This handles cases where rating_avg exists but isn't a number
      return typeof rating === 'number' ? rating : 0
    } catch (e) {
      // Why we use try-catch:
      // - JSON.parse can throw SyntaxError for malformed JSON
      // - Accessing nested properties could throw if review isn't an object
      // - We want to gracefully handle these cases rather than crash
      // - Log
    }
  }
  const ratingValue = getProductRating(attributes)
  return (
    <div className="min-h-[inherit]">
      <div
        className={`prod-card min-h-[inherit] bg-white-- flex flex-col h-full relative-- overflow-visible rounded-lg`}
        data-testid={`${page}-element-${uniqueKey}-product-wrap`}
      >
        <div
          className="flex flex-col items-start tags absolute gap-1"
          data-testid={`${page}-tag-${uniqueKey}-parentElement`}
        >
          {isNewArrival && (
            <span
              className={`text-[10px] md:text-[12px] font-medium z-10 w-auto text-center px-2 py-0.5 text-brand-blue border bg-fill-whiteOpacity rounded-[15px] border-solid border-brand-blue left-2.5 top-2.5`}
              data-testid={`${page}-tag-${uniqueKey}-new-arrivals`}
            >
              {/* New Arrivals */}
              {translate('text-new-arrivals')}
            </span>
          )}

          {isRebateEligible && (
            <span
              className={`text-[10px] md:text-[12px] font-medium z-10 w-auto text-center px-2 py-0.5 text-orange border bg-fill-whiteOpacity rounded-[15px] border-solid border-orange left-2.5 top-10`}
              data-testid={`${page}-tag-${uniqueKey}-rebate-eligible`}
            >
              {translate('text-rebate-eligible')}
            </span>
          )}

          {isFreeShipping && (
            <span
              className={`text-[10px] md:text-[12px] font-medium z-10 w-auto text-center px-2 py-0.5 text-brand-green border bg-fill-whiteOpacity rounded-[15px] border-solid border-green left-2.5 top-[70px]`}
              data-testid={`${page}-tag-${uniqueKey}-free-shipping`}
            >
              {/* Free Shipping */}
              {translate('text-free-shipping')}
            </span>
          )}

          {isClearance && (
            <span
              className={`text-[10px] md:text-[12px] font-medium z-10 w-auto text-center px-2 py-0.5 text-brand-pink border bg-fill-whiteOpacity rounded-[15px] border-solid border-brand-pink left-2.5 top-[70px]`}
              data-testid={`${page}-tag-${uniqueKey}-liquidation`}
            >
              {/* Clearance */}
              {translate('text-liquidation')}
            </span>
          )}
        </div>

        <div className="p-2.5 md:px-4 px-2.5">
          <div
            className="flex overflow-hidden w-auto max-w-[230px] h-auto max-h-[150px] mx-auto transition duration-200 ease-in-out transform group-hover:scale-105 relative mb-2"
            style={{ aspectRatio: '23 / 20' }}
          >
            <Image
              src={
                ((product && product['image.thumbnail']) ||
                  (gallery && gallery?.[0]?.thumbnail)) ??
                productPlaceholder
              }
              alt={name || 'Product Image'}
              fill={true}
              quality={100}
              loading="lazy"
              className="object-cover bg-fill-thumbnail"
              data-testid={`${page}-img-${uniqueKey}-product-image`}
            />

            {isInStock === false && (
              <div
                className={`flex flex-col items-center justify-center absolute text-white w-full h-16 text-xl font-medium leading-5 rounded-sm left-0 top-2/4 bg-black-100 font-sofia-pro`}
              >
                <h4 className="mb-0 text-center">
                  {translate('text-out-of-stock')}
                </h4>
              </div>
            )}
          </div>
          <h2
            title={CommonProductTitle(product)}
            className={`mb-0 md:mb-1 line-clamp-2 text-xl font-medium normal-case text-brand-dark max-h-[45px] md:max-h-[50px] leading-4 lg:leading-6`}
            data-testid={`${page}-text-${uniqueKey}-product-title-parent`}
          >
            <Link
              href={productUrl}
              onClick={navigateToProductPage}
              className="hover:underline hover:font-semibold capitalize text-13px sm:text-sm lg:text-15px"
              data-testid={`${page}-link-${uniqueKey}-product-title`}
            >
              {' '}
              {brand && name && name.indexOf(String(brand)) === -1
                ? String(brand).toLocaleLowerCase()
                : ''}{' '}
              {String(name).toLocaleLowerCase()}
            </Link>
          </h2>

          <span
            className={`relative text-[11px] sm:text-[12px] whitespace-nowrap xsp:whitespace-normal font-medium leading-[21px] text-brand-gray font-poppins`}
            style={{
              top: '-3px',
            }}
            data-testid={`${page}-text-${uniqueKey}-product-per-case`}
          >
            {case_quantity} {translate('text-per-case')}
          </span>

          <div
            className="flex items-center -ml-0.5 mt-0 mb-2"
            data-testid={`${page}-img-${uniqueKey}-start-rating`}
          >
            <StarRatingComponent
              name="prouductcard"
              starCount={5}
              value={ratingValue}
              starColor="#FCA120"
              editing={false}
              emptyStarColor="#DFE6ED"
              renderStarIcon={() => (
                <IoStarSharp
                  fontSize={19}
                  className="-mr-1 !text-[14px] md:!text-[17px]"
                />
              )}
            />
          </div>

          {isSpecialOrderFlag && (
            <div className={`mb-1 md:mb-3--`}>
              <div className="inline-flex relative">
                <Image
                  priority
                  src="/assets/images/special-order-tag.svg"
                  width={75}
                  height={16}
                  alt="Special Order"
                  className="w-[75px] md:w-[91px]"
                  data-testid={`${page}-img-${uniqueKey}-special-order`}
                />
              </div>
            </div>
          )}

          {/* )} */}
          <p className="text-[10px] md:text-[12px] font-[500] !mb-[2.85rem] leading-[18px]">
            {translate('text-ready-to-ship')}{' '}
            <span
              className={`${
                isSpecialOrderFlag ? 'text-yellow' : 'text-brand-green'
              }`}
              data-testid={`${page}-text-${uniqueKey}-ready-to-ship-value`}
            >
              {shipsInDays?.value} {shipsInDays?.unit}(s)
            </span>
          </p>

          <div
            className="w-full-- md:pl-[4px] absolute bottom-[8px] left-[10px]"
            data-testid={`${page}-text-${uniqueKey}-price-parent`}
          >
            {oldPrice && (
              <div className="max-400:mb-1  sm:-mb-2">
                <del
                  className="text-[14px] max-400:text-[11px] font-medium text-brand-gray"
                  data-testid={`${page}-text-${uniqueKey}-old-price`}
                >
                  {formattedOldPrice.replace('CA', '')}
                </del>
              </div>
            )}

            <div className="xs:leading-[14px] sm:!leading-[33px]  xs:flex-col xs:items-start sm:!items-baseline xs:-mb-[4px] sm:!flex-row flex  -mt-1">
              <span
                className={`max-400:text-[14px] text-[17px] sm:text-[20px] font-medium tracking-[-0.01em] text-grey-960 font-poppins`}
                data-testid={`${page}-text-${uniqueKey}-current-price`}
              >
                {formattedCurrentPrice.replace('CA', '')}
              </span>

              <span
                className={`ml-1 italic relative max-400:text-[8px] text-[10px] sm:text-[12px] whitespace-nowrap xsp:whitespace-normal font-medium leading-[21px] text-grey-960 font-poppins`}
                style={{
                  top: '-3px',
                }}
                data-testid={`${page}-text-${uniqueKey}-case`}
              >
                / {displaySoldAsText}
              </span>
            </div>

            {/* {isBuyMoreSaveMore && (
              <div className="flex mb-1">
                <span
                  className={`text-brand-darkblue rounded text-xs font-medium inline-flex items-center border px-[3px] py-1 border-dashed border-grey-900`}
                >
                  <Image
                    className=""
                    style={{ marginRight: '2px' }}
                    priority
                    src="/assets/images/buy-more-dollar-tag.svg"
                    width={12}
                    height={12}
                    alt="Buy More Dollar Tag"
                  />
                  <span
                    className={`relative text-[8px] font-medium leading-3 text-grey-960`}
                    style={{ top: '1px' }}
                  >
                    Buy More, Save More
                  </span>
                </span>
              </div>
            )} */}
          </div>
        </div>

        <div className="absolute bottom-0 lg:bottom-0 right-0 mt-auto pt-0 flex justify-between items-center">
          <div
            className={`block-- product-count-button-position-- relative w-[181px] flex justify-end items-center`}
            style={{
              right: '10px',
              bottom: '10px',
            }}
            onClick={(e) => e.stopPropagation()} // Prevent event bubbling
            data-testid={`${page}-btn-${uniqueKey}-counter-parent`}
          >
            {isInStock && (
              <Counter
                loading={addToCartLoader}
                variant="card"
                value={selectedQuantity}
                addToCart={addToCart}
                onDecrement={() =>
                  setSelectedQuantity((prev) => (prev !== 1 ? prev - 1 : 1))
                }
                onInputChange={inputChangeHandler}
                dataTestId={`${page}-btn-${uniqueKey}-counter`}
                data={product}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ProductCardNew: FC<ProductProps> = ({
  product,
  className,
  page = 'home',
  uniqueKey = '',
  resetTrigger,
  allowModal = false,
}) => {
  const router = useRouter()
  const { locale } = router

  const {
    name,
    image,
    product_type,
    brand,
    case_quantity,
    gallery,
    slug,
    url,
  } = convertProduct(product, locale) ?? {}
  const { openModal } = useModalAction()

  const { price, basePrice, discount } = usePrice({
    amount: product?.sale_price ? product?.sale_price : product?.price,
    baseAmount: product?.price,
    currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
  })
  const { price: minPrice } = usePrice({
    amount: product?.min_price ?? 0,
    currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
  })
  const { price: maxPrice } = usePrice({
    amount: product?.max_price ?? 0,
    currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
  })

  const productUrl = `/${slug || url}`

  function handlePopupView() {
    if (allowModal) {
      openModal('PRODUCT_VIEW', product)
      return
    }
    if (router.pathname === '/[product_pdp]') {
      window.location.href = productUrl
    } else openModal('PRODUCT_VIEW', product)
  }

  return (
    <article
      className={`${cn(
        'block group rounded-md cursor-pointer transition-all duration-300 h-full overflow-visible',
        className,
      )}`}
      onClick={handlePopupView}
      title={name}
    >
      <ProductCard
        product={product}
        gallery={gallery}
        name={name}
        brand={brand}
        productUrl={productUrl}
        product_type={product_type}
        basePrice={basePrice}
        minPrice={minPrice}
        maxPrice={maxPrice}
        page={page}
        uniqueKey={uniqueKey}
        resetTrigger={resetTrigger}
      />
    </article>
  )
}
export default ProductCardNew
